var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Badge } from "../../tremor/Badge";
import { Label, Metric } from "../../tremor/Text";
import { Button } from "../../tremor/Button";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDashboard } from "../../layouts/Dashboard/useDashboard";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { AdjustmentsHorizontalIcon, ArrowDownTrayIcon, ArrowPathIcon, ChevronDownIcon, ClockIcon, DocumentChartBarIcon, PhotoIcon, PresentationChartBarIcon, } from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { useBackend } from "../../layouts/Wrapper";
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuIconWrapper, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger, } from "../../tremor/DropdownMenu";
import { useTheme } from "../../layouts/Dashboard/useTheme";
import { AutomationsModal } from "../AutomationsModal";
import { Popover, PopoverContent, PopoverTrigger } from "../../tremor/Popover";
import { FilterBar } from "../FilterBar";
import { useAutomationsModal } from "../AutomationsModal/useAutomationsModal";
import { Tooltip } from "../../tremor/Tooltip";
dayjs.extend(relativeTime);
function formatList(items) {
    if (items.length === 0)
        return React.createElement(React.Fragment, null);
    if (items.length === 1)
        return React.createElement(Badge, { variant: "neutral", className: "!onvo-py-0 onvo-mb-1" }, items[0]);
    if (items.length === 2)
        return React.createElement(React.Fragment, null,
            React.createElement(Badge, { variant: "neutral", className: "!onvo-py-0 onvo-mb-1" }, items[0]),
            " and ",
            React.createElement(Badge, { variant: "neutral", className: "!onvo-py-0 onvo-mb-1" }, items[1]));
    return React.createElement(React.Fragment, null,
        items.slice(0, -1).map(a => React.createElement(Badge, { variant: "neutral", className: "!onvo-py-0 onvo-mb-1 onvo-mr-1" }, a)),
        "and ",
        React.createElement(Badge, { variant: "neutral", className: "!onvo-py-0 onvo-mb-1" }, items[items.length - 1]));
}
export const LastUpdatedBadge = ({ date, refreshing }) => {
    const [key, setKey] = useState(date);
    useEffect(() => {
        let interval = setInterval(() => {
            setKey(new Date().toISOString());
        }, 1000);
        return () => {
            clearInterval(interval);
        };
    }, []);
    if (refreshing) {
        return (React.createElement(Badge, { key: "refreshing-badge", className: "onvo-last-updated-badge ml-2" },
            React.createElement(ArrowPathIcon, { className: "onvo-size-3 onvo-animate-spin" }),
            "Auto refreshing..."));
    }
    return (React.createElement(Badge, { key: key, className: "onvo-last-updated-badge ml-2" },
        "Updated ",
        dayjs(date).fromNow()));
};
export const DashboardHeader = ({ children, className, variant }) => {
    var _a, _b, _c, _d, _e;
    const { setOpen } = useAutomationsModal();
    const { dashboard, refreshing, tab, setTab } = useDashboard();
    const theme = useTheme();
    const { backend, adminMode, containerRef } = useBackend();
    const exportDashboard = useCallback((format) => {
        if (!backend || !dashboard) {
            return;
        }
        toast.promise(() => __awaiter(void 0, void 0, void 0, function* () {
            const blob = yield backend.dashboard(dashboard.id).export(format, theme, tab);
            return window.URL.createObjectURL(blob);
        }), {
            loading: `Exporting dashboard as ${format}...`,
            success: (url) => {
                let a = document.createElement("a");
                if (format === "csv") {
                    a.download = dashboard.title + ".zip";
                }
                else {
                    a.download = dashboard.title + "." + format;
                }
                a.href = url;
                document.body.appendChild(a);
                a.click();
                a.remove();
                return "Dashboard exported successfully";
            },
            error: (e) => {
                return "Failed to export dashboard: " + e.message;
            },
        });
    }, [dashboard, backend, tab]);
    const ImageDownloadEnabled = useMemo(() => {
        if ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) && dashboard.settings.disable_download_images)
            return false;
        return true;
    }, [dashboard]);
    const DocumentDownloadEnabled = useMemo(() => {
        if ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) && dashboard.settings.disable_download_documents)
            return false;
        return true;
    }, [dashboard]);
    const ReportDownloadEnabled = useMemo(() => {
        if ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) && dashboard.settings.disable_download_reports)
            return false;
        return true;
    }, [dashboard]);
    const filtered = useMemo(() => {
        let filters = (dashboard === null || dashboard === void 0 ? void 0 : dashboard.filters) || [];
        if (filters.length === 0)
            return [];
        let results = [];
        for (let filter of filters) {
            if (filter.type === "text" && filter.options.length !== filter.values.length) {
                results.push(filter.label);
            }
            if (filter.type === "number" && (filter.options[0] !== filter.values[0] || filter.options[1] !== filter.values[1])) {
                results.push(filter.label);
            }
            if (filter.type === "date" && (filter.options[0] !== filter.values[0] || filter.options[1] !== filter.values[1])) {
                results.push(filter.label);
            }
        }
        return results;
    }, [dashboard]);
    if (((_a = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _a === void 0 ? void 0 : _a.hide_header) && !adminMode)
        return React.createElement(React.Fragment, null);
    const DownloadDropdown = (React.createElement(DropdownMenu, { modal: false },
        React.createElement(DropdownMenuTrigger, { asChild: true },
            React.createElement(Button, { className: "onvo-foreground-color onvo-border-black/10 dark:onvo-border-white/10", variant: "secondary", disabled: !dashboard },
                React.createElement(ArrowDownTrayIcon, { className: "onvo-size-4 onvo-mr-1" }),
                "Download",
                React.createElement(ChevronDownIcon, { className: "onvo-h-4 onvo-w-4 onvo-ml-1" }))),
        React.createElement(DropdownMenuContent, { container: containerRef === null || containerRef === void 0 ? void 0 : containerRef.current, className: "onvo-min-w-56 onvo-background-color onvo-border-black/10 dark:onvo-border-white/10" },
            ReportDownloadEnabled && (React.createElement(React.Fragment, null,
                React.createElement(DropdownMenuLabel, null, "Report formats"),
                React.createElement(DropdownMenuGroup, null,
                    React.createElement(DropdownMenuItem, { onClick: () => exportDashboard("xlsx") },
                        React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                            React.createElement(DocumentChartBarIcon, { className: "onvo-size-4" }),
                            React.createElement("span", null, "Excel"))),
                    React.createElement(DropdownMenuItem, { onClick: () => exportDashboard("csv") },
                        React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                            React.createElement(DropdownMenuIconWrapper, null,
                                React.createElement(DocumentChartBarIcon, { className: "onvo-size-4" })),
                            React.createElement("span", null, "CSV")))))),
            DocumentDownloadEnabled && ReportDownloadEnabled && (React.createElement(DropdownMenuSeparator, { className: "onvo-border-black/10 dark:onvo-border-white/10" })),
            DocumentDownloadEnabled && (React.createElement(React.Fragment, null,
                React.createElement(DropdownMenuLabel, null, "Document formats"),
                React.createElement(DropdownMenuGroup, null,
                    React.createElement(DropdownMenuItem, { onClick: () => exportDashboard("pdf") },
                        React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                            React.createElement(DocumentChartBarIcon, { className: "onvo-size-4" }),
                            React.createElement("span", null, "PDF"))),
                    React.createElement(DropdownMenuItem, { onClick: () => exportDashboard("pptx") },
                        React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                            React.createElement(PresentationChartBarIcon, { className: "onvo-size-4" }),
                            React.createElement("span", null, "Powerpoint")))))),
            ImageDownloadEnabled && (DocumentDownloadEnabled || ReportDownloadEnabled) && (React.createElement(DropdownMenuSeparator, { className: "onvo-border-black/10 dark:onvo-border-white/10" })),
            ImageDownloadEnabled && (React.createElement(React.Fragment, null,
                React.createElement(DropdownMenuLabel, null, "Image formats"),
                React.createElement(DropdownMenuGroup, null,
                    React.createElement(DropdownMenuItem, { onClick: () => exportDashboard("png") },
                        React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                            React.createElement(DropdownMenuIconWrapper, null,
                                React.createElement(PhotoIcon, { className: "onvo-size-4" })),
                            React.createElement("span", null, "PNG"))),
                    React.createElement(DropdownMenuItem, { onClick: () => exportDashboard("jpeg") },
                        React.createElement("span", { className: "onvo-flex onvo-items-center onvo-gap-x-2" },
                            React.createElement(DropdownMenuIconWrapper, null,
                                React.createElement(PhotoIcon, { className: "onvo-size-4" })),
                            React.createElement("span", null, "JPEG")))))))));
    if (variant === "pdf" || variant === "pptx") {
        return (React.createElement("section", { className: `onvo-@container/onvo-dashboard-header onvo-leading-tight onvo-bg-slate-900 onvo-dashboard-header onvo-sticky onvo-z-50 onvo-top-0 ${className ? className : ""}` },
            React.createElement("div", { className: "onvo-flex onvo-py-2 onvo-px-5 onvo-flex-row onvo-justify-between onvo-items-center" },
                ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.tabs) || []).filter(a => !a.hidden).length > 1 ? (React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-gap-2" }, ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.tabs) || []).filter(a => !a.hidden).map(a => (React.createElement(Button, { key: a.id + "", variant: a.id === tab ? "light" : "secondary", onClick: () => {
                        setTab(a.id);
                    } }, a.label))))) : React.createElement("div", null),
                (ImageDownloadEnabled || ReportDownloadEnabled || DocumentDownloadEnabled) &&
                    DownloadDropdown)));
    }
    return (React.createElement("section", { className: `onvo-@container/onvo-dashboard-header onvo-leading-tight onvo-background-color onvo-dashboard-header onvo-sticky onvo-z-50 onvo-top-0 ${className ? className : ""}`, style: {
            boxShadow: `0px 8px 4px ${theme === "dark" ? (_b = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _b === void 0 ? void 0 : _b.dark_background : (_c = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _c === void 0 ? void 0 : _c.light_background}`,
        } },
        React.createElement(AutomationsModal, null),
        React.createElement("main", { className: "onvo-mx-auto onvo-px-5 onvo-pt-4 onvo-pb-2 onvo-z-10 onvo-relative onvo-flex onvo-flex-col onvo-items-start onvo-justify-between onvo-gap-4 @lg/onvo-dashboard-header:onvo-flex-row @lg/onvo-dashboard-header:onvo-items-center" },
            dashboard && dashboard.thumbnail && dashboard.thumbnail.trim() !== "" && (React.createElement("div", { className: "onvo-relative onvo-h-12 onvo-w-12 onvo-flex-shrink-0 onvo-rounded-md onvo-border onvo-border-slate-200 onvo-bg-slate-50 dark:onvo-border-slate-700 dark:onvo-bg-slate-900" },
                React.createElement("img", { className: "onvo-rounded-md onvo-onvo-size-12 onvo-bg-slate-50 onvo-object-contain dark:onvo-bg-slate-900", src: dashboard.thumbnail, alt: dashboard.title }))),
            React.createElement("div", { className: "onvo-flex-grow" }, dashboard ? (React.createElement(React.Fragment, null,
                React.createElement(Metric, { className: "onvo-dashboard-header-title !onvo-font-semibold dark:!onvo-text-white !onvo-text-black onvo-font-override onvo-text-xl" }, (dashboard === null || dashboard === void 0 ? void 0 : dashboard.title) || " "),
                React.createElement(Label, { className: "onvo-dashboard-header-description !onvo-font-normal onvo-font-override" },
                    (dashboard === null || dashboard === void 0 ? void 0 : dashboard.description) || " ",
                    " ",
                    React.createElement(LastUpdatedBadge, { refreshing: refreshing, date: dashboard.last_updated_at })))) : (React.createElement("div", { className: "onvo-w-3/5 onvo-animate-pulse" },
                React.createElement("div", { className: "onvo-mb-2 onvo-h-6 onvo-w-4/5 onvo-rounded-md onvo-bg-gray-100 dark:onvo-bg-gray-700" }),
                React.createElement("div", { className: "onvo-h-4 onvo-w-2/5 onvo-rounded-md onvo-bg-gray-100 dark:onvo-bg-gray-700" })))),
            React.createElement("div", { className: "onvo-flex onvo-flex-row onvo-gap-2" },
                ((_d = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _d === void 0 ? void 0 : _d.filters) && dashboard.filters.length > 0 && (React.createElement(Popover, null,
                    React.createElement(PopoverTrigger, { asChild: true },
                        React.createElement(Button, { className: "onvo-relative onvo-foreground-color onvo-border-black/10 dark:onvo-border-white/10", variant: "secondary" },
                            React.createElement(AdjustmentsHorizontalIcon, { className: "onvo-size-4 onvo-mr-1" }),
                            "Filters",
                            (filtered === null || filtered === void 0 ? void 0 : filtered.length) > 0 && (React.createElement(React.Fragment, null,
                                React.createElement("div", { className: "onvo-size-2" }),
                                React.createElement(Tooltip, { side: "bottom", sideOffset: 20, content: React.createElement("p", { className: "onvo-text-xs" },
                                        "The dashboard is currently filtered by ",
                                        formatList(filtered)) },
                                    React.createElement("div", { className: "onvo-absolute -onvo-top-2 -onvo-right-2 onvo-rounded-full onvo-bg-red-500 onvo-text-white onvo-text-xs onvo-text-center onvo-font-bold onvo-size-5 onvo-flex onvo-items-center onvo-justify-center" }, filtered === null || filtered === void 0 ? void 0 : filtered.length)))))),
                    React.createElement(PopoverContent, { onInteractOutside: e => e.preventDefault(), container: containerRef === null || containerRef === void 0 ? void 0 : containerRef.current, className: "!onvo-p-0 onvo-z-50 onvo-w-[320px]" },
                        React.createElement(FilterBar, null)))),
                ((_e = dashboard === null || dashboard === void 0 ? void 0 : dashboard.settings) === null || _e === void 0 ? void 0 : _e.can_schedule_reports) && (React.createElement(Button, { className: "onvo-foreground-color onvo-border-black/10 dark:onvo-border-white/10", variant: "secondary", onClick: () => {
                        setOpen(true);
                    } },
                    React.createElement(ClockIcon, { className: "onvo-size-4 onvo-mr-1" }),
                    "Schedule")),
                (ImageDownloadEnabled || ReportDownloadEnabled || DocumentDownloadEnabled) &&
                    DownloadDropdown,
                children)),
        ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.tabs) || []).filter(a => !a.hidden).length > 1 && (React.createElement("div", { className: "onvo-px-5 onvo-pb-2 onvo-flex onvo-flex-row onvo-gap-2" }, ((dashboard === null || dashboard === void 0 ? void 0 : dashboard.tabs) || []).filter(a => !a.hidden).map(a => (React.createElement(Button, { key: a.id + "", className: "!onvo-rounded-full", variant: a.id === tab ? "light" : "secondary", onClick: () => {
                setTab(a.id);
            } }, a.label)))))));
};
//# sourceMappingURL=DashboardHeader.js.map